import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, ScrollView } from 'react-native'

import { useIsFocused } from '@react-navigation/native'
import { FlashList } from '@shopify/flash-list'
import { useQueryClient } from '@tanstack/react-query'
import { CopilotProvider } from '@zet/common/components/coachMarks'
import TransparentHeader from '@zet/common/components/header/transparentHeader'
import LazyHOC from '@zet/common/components/lazyHoc'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  Button,
  colors,
  Icon,
  Input,
  Stack,
  colors as zetColors,
} from '@zet/design-system'
import { APP_NAME } from '@zet/end-points'
import {
  useFetchFilterFields,
  useLeadCount,
  useLeads,
} from '@zet/feature-leads/api/leads/hooks'
import EmptyLeads from '@zet/feature-leads/components/emptyLeads'
import { LeadCard } from '@zet/feature-leads/components/leadCard'
import LeadReportDownload from '@zet/feature-leads/components/leadReportDownload'
import useLeadFilterStore from '@zet/feature-leads/store/leadFilter'
import { resetQuickFilters } from '@zet/feature-leads/utils/resetQuickFilters'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { QuickFilter } from '@zet/utils/Constants'
import { useFetchRef } from '@zet/utils/useFetchRef'
import { LinearGradient } from 'expo-linear-gradient'
import _ from 'lodash'
import moment from 'moment'

import LeadsFilterTab from '../../components/leadsFilterTab'
import LeadsSummary from './LeadSummary'
import LeadCardSkeleton from './layout/LeadCardSkeleton'

const renderItem = ({ item, noOfLeads, isFetching }: any) => {
  return (
    <LeadCard
      lastUpdateDate={moment.utc(item?.lastUpdatedDate).format('DD-MMM-YYYY')}
      nextUpdateDate={moment.utc(item?.nextUpdateDate).format('DD-MMM-YYYY')}
      name={item?.name}
      phoneNumber={item?.phoneNumber}
      status={item?.status}
      isBonusLead={item?.isBonusLead}
      subAgentName={item?.subAgentName}
      hasNotMoved={item?.hasNoUpdate}
      paymentStatus={item?.partnerTransaction?.[0].status ?? ''}
      rejectionReason={item?.rejectionReason}
      pendingDays={item?.expiryDays}
      isDelayedByBrand={item?.isReportDelayed}
      brandCommission={item?.brandCommission}
      commissionType={item?.commissionType}
      commission={item?.partnerTransaction?.[0]?.commission ?? ''}
      title={item?.applicationAsks?.[0]?.title ?? ''}
      description={item?.applicationAsks?.[0]?.description ?? ''}
      isReopenAllowed={item?.isReopenAllowed}
      isShareNewLink={item?.isShareNewLink}
      isReopened={item?.isReopened}
      id={item?.id}
      partnerId={item?.partnerId}
      eventName={'click_action'}
      eventPayload={{
        item_name: 'my_leads_lead_card',
        lead_id: item?.id,
        lead_status: item?.status,
        partner_id: item?.partnerId,
      }}
      askCode={item?.applicationAsks?.[0]?.askCode}
      extraCommission={item?.extraCommission}
      isMagnetEligible={item?.isMagnetEligible}
      showCrossSelling={item?.showCrossSelling}
      crossSellingPartnerName={item?.crossSellingPartnerName}
      noOfLeads={noOfLeads ?? 0}
      isFetching={isFetching}
      source={item?.source}
    />
  )
}

type LeadParams = {
  route: {
    params: {
      categoryId?: string
      leadStatus?: string
      applicationStatus?: string
      partnerId?: string
      leadsType?: string
    }
  }
}

const Leads = ({ route, showHeader = true, leadsType = null }: LeadParams) => {
  const [searchedText, setSearchedText] = useState<string>('')
  const [leadReportSheetVisible, setLeadReportSheetVisible] = useState(false)
  const [isRefresh, setIsRefresh] = useState<boolean>(false)
  const leadFilterStore = useLeadFilterStore()
  const isFocused = useIsFocused()
  const { setParams } = useNavigation()
  const { data: user } = useUserContext()

  const isRouteFromTab = useRef(false)

  const {
    data: leadsData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isLoading,
  } = useLeads({
    searchText: searchedText?.trim(),
    leadsType: leadsType || route?.params?.leadsType,
    limit: 10,
  })

  const leadCardList =
    leadsData?.pages
      ?.map((page) => page?.leads)
      .flat()
      .filter((item) => Boolean(item)) || []

  const { data: filters = [] } = useFetchFilterFields(leadsType)
  const queryClient = useQueryClient()
  const { data: leadsCountData } = useLeadCount()

  const emptyFun = () => {
    return false
  }

  useFetchRef(route?.params?.leadStatus ? emptyFun : refetch)

  useEffect(() => {
    if (!isFocused) {
      resetFilters()
      setSearchedText('')
      queryClient.invalidateQueries({
        queryKey: ['leads'],
        refetchType: 'none',
      })
    }
  }, [isFocused])

  const renderQuickFilters = () => {
    return Object.keys(filters).map((item, index) => (
      <LeadsFilterTab
        key={item}
        index={index}
        length={Object.keys(filters).length}
        filterName={item}
        filterOptions={filters[item]}
      />
    ))
  }

  const debouncedFetch = useCallback(
    _.debounce((query) => {
      setSearchedText(query)
    }, 600),
    [refetch],
  )

  const handleInputChange = (query: string) => {
    debouncedFetch(query)
  }

  const resetFilters = () => {
    leadFilterStore.resetLeadFilters()
    resetQuickFilters()
    setParams({
      leadStatus: '',
    })
  }

  const clearFilters = () => {
    resetFilters()
    refetch()
  }

  useEffect(() => {
    return () => {
      resetLeadsType()
      refetch()
    }
  }, [isRouteFromTab.current])

  const resetLeadsType = () => {
    setParams({
      leadsType: '',
    })
  }

  const checkProfileStatusFilter = () => {
    if (route.params?.leadStatus) {
      leadFilterStore.updateFilter({
        profileStatus:
          route.params?.leadStatus === 'total'
            ? []
            : [...route.params.leadStatus.split('-')],
      })
      QuickFilter['profileStatus'].value =
        route.params?.leadStatus === 'total'
          ? []
          : [...route.params.leadStatus.split('-')]
    }
  }

  const checkApplicationStatusFilter = () => {
    if (route.params?.applicationStatus) {
      leadFilterStore.updateFilter({
        stage: [...route.params.applicationStatus.split('-')],
      })
      QuickFilter['stage'].value = [
        ...route.params.applicationStatus.split('-'),
      ]
    }
  }

  const checkPartnerFilter = () => {
    if (route.params?.partnerId) {
      leadFilterStore.updateFilter({
        brands: [...route.params.partnerId.split('-')],
      })
      QuickFilter['brands'].value = [...route.params.partnerId.split('-')]
    }
  }

  const checkCategoryFilter = () => {
    if (route.params?.categoryId) {
      leadFilterStore.updateFilter({
        category: [...route.params.categoryId.split('-')],
      })
      QuickFilter['category'].value = [...route.params.categoryId.split('-')]
    }
  }

  const handleNavigationFilters = () => {
    resetFilters()
    checkProfileStatusFilter()
    checkApplicationStatusFilter()
    checkPartnerFilter()
    checkCategoryFilter()
    !route?.params?.leadStatus && refetch()
  }

  useEffect(() => {
    if (
      isFocused &&
      (route.params?.applicationStatus ||
        route.params?.partnerId ||
        route.params?.categoryId ||
        route.params?.leadStatus)
    ) {
      handleNavigationFilters()
      // isRouteFromTab.current = false
    }
  }, [isFocused])

  return (
    <>
      <CopilotProvider>
        <Stack className="bg-grays-20 flex-1">
          {showHeader ? (
            <LinearGradient
              colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
              start={[0, 0]}
              end={[1, 0.0]}
              locations={[0.0, 1.0]}
            >
              <TransparentHeader
                title={i18n.t('myLeads.title')}
                badge={
                  APP_NAME !== MY_TEAMS_ADVISOR ? leadsCountData?.total : 0
                }
                headerRightButton={
                  <Button
                    iconLeft={
                      <Icon color={zetColors.secondary[50]} type="Download" />
                    }
                    className="px-0"
                    textStyle="text-white ml-[2] text-secondary-50 "
                    textOnly={true}
                    onPress={() => setLeadReportSheetVisible(true)}
                    eventName="click_action"
                    eventPayload={{
                      item_name: 'my_leads_download_report',
                      user_id: user?.id,
                    }}
                  >
                    {i18n.t('myLeads.downloadReport')}
                  </Button>
                }
              />
              {APP_NAME !== MY_TEAMS_ADVISOR && (
                <LeadsSummary refetch={refetch} />
              )}
              <LazyHOC>
                <Stack className="mt-2 px-3">
                  <Input
                    iconLeft={
                      <Icon
                        style={{
                          marginTop: -12,
                        }}
                        type="SearchOutlined"
                        color="white"
                      />
                    }
                    color="#FFFFFF"
                    selectionColor={'grey'}
                    className=" mb-3 border-[#f5f7fb7c] bg-[#1A304D]"
                    placeholder={i18n.t('myCustomers.placeholder')}
                    frameStyle={{
                      backgroundColor: '#1A304D',
                      borderColor: '#f5f7fb7c',
                      marginBottom: 16,
                    }}
                    style={
                      Platform.OS === 'web'
                        ? { color: zetColors.neutral.white100 }
                        : {}
                    }
                    onChangeText={handleInputChange}
                  />
                </Stack>
              </LazyHOC>
            </LinearGradient>
          ) : (
            <></>
          )}
          <LazyHOC>
            {filters && (
              <ScrollView
                showsHorizontalScrollIndicator={false}
                horizontal={true}
                className="bg-neutral-white100 flex-grow-0 pt-1"
                keyboardDismissMode="on-drag"
              >
                {renderQuickFilters()}
              </ScrollView>
            )}
            {isLoading ? (
              <Stack className="flex-1 bg-white py-2">
                {Array(4)
                  .fill({})
                  .map((_, index) => (
                    <LeadCardSkeleton key={index} />
                  ))}
              </Stack>
            ) : (
              <FlashList
                keyboardDismissMode="on-drag"
                keyboardShouldPersistTaps="always"
                estimatedItemSize={250}
                data={leadCardList}
                renderItem={({ item }) =>
                  renderItem({
                    item,
                    noOfLeads: leadCardList.length,
                    isFetching,
                  })
                }
                keyExtractor={(item, index) => String(item?.id ?? index)}
                ListEmptyComponent={
                  !isFetching ? (
                    <EmptyLeads
                      withFilter={!_.isEmpty(leadFilterStore?.leadFilters)}
                      clearFilters={clearFilters}
                    />
                  ) : null
                }
                ListFooterComponent={
                  isFetchingNextPage ? (
                    <Stack className="bg-white py-2">
                      <LeadCardSkeleton />
                    </Stack>
                  ) : null
                }
                ItemSeparatorComponent={() => (
                  <Stack
                    style={{ height: 8, backgroundColor: colors.grays[30] }}
                  />
                )}
                contentContainerStyle={{
                  backgroundColor: '#fff',
                  paddingBottom: 16,
                }}
                onEndReached={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage()
                  }
                }}
                onRefresh={() => {
                  setIsRefresh(true)
                  refetch().finally(() => setIsRefresh(false))
                }}
                refreshing={isRefresh}
                showsVerticalScrollIndicator={false}
              />
            )}
            {leadReportSheetVisible && (
              <LeadReportDownload
                isVisible={leadReportSheetVisible}
                hideBottomSheet={() => setLeadReportSheetVisible(false)}
                sheetName="my_leads_download_report"
              />
            )}
          </LazyHOC>
        </Stack>
      </CopilotProvider>
    </>
  )
}

export { Leads }
